import { Injectable, signal, computed } from '@angular/core';
import {
    ONBOARDING_STATE_VERSION,
    etatApresChangementDeJuridiction,
    lireEtatPersiste,
} from './onboarding-state.persistance';

export interface JurisdictionMeta {
    timezone?: string;
    defaultLanguage?: string;
    defaultCurrency?: string;
    /**
     * Le code du PAYS de la juridiction choisie (`CA` pour `CA-QC`, `HT` pour
     * `HT`). C'est lui que le serveur ecrit dans `companies.country` et qui
     * designe les modeles de reference ; la province, elle, est dans
     * `jurisdictionCode`.
     */
    countryCode?: string;
}

export interface OnboardingState {
    /**
     * Version du format persiste dans `localStorage`. Un etat ecrit par une
     * version anterieure est purge a la lecture : voir
     * `onboarding-state.persistance.ts`.
     */
    version: number;
    currentStep: number;
    jurisdictionCode: string | null;
    jurisdictionMeta: JurisdictionMeta;
    /**
     * LE SECTEUR VIT AVEC LA JURIDICTION, PAS DANS L'IDENTITE.
     *
     * Il etait range dans `identity`, saisi a l'etape 2 dans un menu perdu au
     * milieu du formulaire. Or c'est le COUPLE (juridiction, secteur) qui
     * choisit le referentiel de la nouvelle entreprise : les separer rendait
     * impossible d'annoncer, a l'etape 1, ce que l'entreprise recevrait.
     *
     * Facultatif : sans secteur, le serveur retombe sur le socle de la
     * juridiction. L'exiger rendrait l'etape infranchissable pour tout metier
     * absent du catalogue.
     */
    activitySectorId: number | null;
    identity: Record<string, unknown>;
    contactLocation: Record<string, unknown>;
    fiscalConfig: Record<string, unknown>;
    subscription: Record<string, unknown>;
    adminConfig: Record<string, unknown>;
    stepsCompleted: Record<number, boolean>;
    startedAt: string;
}

const STORAGE_KEY = 'sf_onboarding_state';
const TOTAL_STEPS = 6;

/** Returns a user-scoped storage key to avoid cross-user contamination */
function getUserStorageKey(): string {
    try {
        const email = localStorage.getItem('sf_onboarding_user');
        return email ? `${STORAGE_KEY}_${email}` : STORAGE_KEY;
    } catch {
        return STORAGE_KEY; // stockage indisponible (mode prive, acces refuse)
    }
}

/** Execute une ecriture dans le stockage ; un stockage indisponible ne doit jamais interrompre l'assistant. */
function sansErreur(ecriture: () => void): void {
    try {
        ecriture();
    } catch { /* l'etat reste en memoire pour la session */ }
}

function createEmptyState(): OnboardingState {
    return {
        version: ONBOARDING_STATE_VERSION,
        currentStep: 1,
        jurisdictionCode: null,
        activitySectorId: null,
        jurisdictionMeta: {},
        identity: {},
        contactLocation: {},
        fiscalConfig: {},
        subscription: {},
        adminConfig: {},
        stepsCompleted: {},
        startedAt: new Date().toISOString(),
    };
}

@Injectable({ providedIn: 'root' })
export class OnboardingStateService {
    private readonly _state = signal<OnboardingState>(this.loadFromStorage());

    readonly state = this._state.asReadonly();
    readonly currentStep = computed(() => this._state().currentStep);
    readonly jurisdictionCode = computed(() => this._state().jurisdictionCode);
    readonly activitySectorId = computed(() => this._state().activitySectorId);
    readonly stepsCompleted = computed(() => this._state().stepsCompleted);
    readonly completedStepNumbers = computed(() =>
        Object.entries(this._state().stepsCompleted)
            .filter(([, done]) => done)
            .map(([step]) => Number(step))
    );

    /**
     * Lit l'etat du navigateur ; ne leve jamais.
     *
     * Un etat d'une version anterieure, illisible ou de forme inattendue est
     * SUPPRIME du stockage (cle de l'utilisateur et cle generique) et remplace
     * par un etat vide : voir `onboarding-state.persistance.ts`.
     */
    private loadFromStorage(): OnboardingState {
        const parDefaut = createEmptyState();
        // User-scoped key first, then fallback to the generic key
        for (const key of new Set([getUserStorageKey(), STORAGE_KEY])) {
            let brut: string | null = null;
            try {
                brut = localStorage.getItem(key);
            } catch {
                continue;
            }
            const lecture = lireEtatPersiste(brut, parDefaut);
            if (lecture.type === 'valide') return lecture.etat;
            if (lecture.type === 'purge') sansErreur(() => localStorage.removeItem(key));
        }
        return parDefaut;
    }

    private persist(): void {
        sansErreur(() => localStorage.setItem(getUserStorageKey(), JSON.stringify(this._state())));
    }

    /** Bind the state to a specific user email (called at login) */
    bindToUser(email: string): void {
        sansErreur(() => {
            localStorage.setItem('sf_onboarding_user', email);
            // Migrate any unscoped state to the user-scoped key
            const generic = localStorage.getItem(STORAGE_KEY);
            if (generic) {
                localStorage.setItem(getUserStorageKey(), generic);
                localStorage.removeItem(STORAGE_KEY);
            }
        });
        // Reload state for this user
        this._state.set(this.loadFromStorage());
    }

    setJurisdiction(code: string, meta?: JurisdictionMeta): void {
        this._state.update(avant => {
            // Un AUTRE code invalide ce que les etapes suivantes ont saisi pour
            // l'ancien (champs d'adresse, identifiants fiscaux, forme juridique).
            const base = avant.jurisdictionCode !== null && avant.jurisdictionCode !== code
                ? etatApresChangementDeJuridiction(avant)
                : avant;
            return {
                ...base,
                jurisdictionCode: code,
                jurisdictionMeta: meta || {},
                stepsCompleted: { ...base.stepsCompleted, 1: true },
                currentStep: 2,
            };
        });
        this.persist();
    }

    setActivitySector(id: number | null): void {
        this._state.update(s => ({ ...s, activitySectorId: id }));
        this.persist();
    }

    updateStep(step: number, data: Record<string, unknown>): void {
        const fieldMap: Record<number, keyof OnboardingState> = {
            2: 'identity',
            3: 'contactLocation',
            4: 'fiscalConfig',
            5: 'subscription',
            6: 'adminConfig',
        };

        const field = fieldMap[step];
        if (!field) return;

        this._state.update(s => ({
            ...s,
            [field]: data,
            stepsCompleted: { ...s.stepsCompleted, [step]: true },
            currentStep: Math.min(step + 1, TOTAL_STEPS),
        }));
        this.persist();
    }

    canAccessStep(step: number): boolean {
        if (step === 1) return true;
        // All previous steps must be completed
        for (let i = 1; i < step; i++) {
            if (!this._state().stepsCompleted[i]) return false;
        }
        return true;
    }

    getCurrentValidStep(): number {
        for (let i = 1; i <= TOTAL_STEPS; i++) {
            if (!this._state().stepsCompleted[i]) return i;
        }
        return TOTAL_STEPS;
    }

    getFullPayload(): OnboardingState {
        return this._state();
    }

    reset(): void {
        const key = getUserStorageKey();
        sansErreur(() => {
            localStorage.removeItem(key);
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem('sf_onboarding_user');
        });
        this._state.set(createEmptyState());
    }
}
